import React, {useEffect, useState} from 'react';
import {Badge, Button, Dropdown, List, message, Modal, Space, Tag, Tooltip, Typography} from 'antd';
import {DeleteOutlined, DownloadOutlined, ExclamationCircleOutlined} from '@ant-design/icons';
import {
  clearFailedFiles,
  FailedFileInfo,
  getFailedFiles,
  removeFailedFile,
  subscribeToUploadState
} from '../../helpers/uploadState';
import {formatFileSize} from '../../helpers/file';
import {downloadToCsv, downloadToExcel} from '../../helpers/csv';
import { useTranslation } from '../../hooks/useTranslation';

const {Text} = Typography;

const FailedFiles: React.FC = () => {
  const { t } = useTranslation();
  const [failedFiles, setFailedFiles] = useState<FailedFileInfo[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // 更新失败文件列表
  const updateFailedFiles = () => {
    setFailedFiles(getFailedFiles());
  };

  useEffect(() => {
    // 初始加载
    updateFailedFiles();
    
    // 订阅状态变化
    const unsubscribe = subscribeToUploadState(updateFailedFiles);
    
    return unsubscribe;
  }, []);

  const handleClearAll = () => {
    clearFailedFiles();
    setIsModalVisible(false);
  };

  const handleRemoveFile = (fileName: string) => {
    removeFailedFile(fileName);
  };


  // 导出失败文件列表
  const handleExport = (type: 'csv' | 'excel') => {
    if (failedFiles.length === 0) {
      message.warning(t('failedFiles.noFilesToExport'));
      return;
    }

    // 准备导出数据
    const exportData = failedFiles.map(file => ({
      fileName: file.fileName,
      errorMessage: file.errorMessage,
      fileSize: file.fileSize ? formatFileSize(file.fileSize) : t('common.unknown'),
      fileType: file.fileType || t('common.unknown'),
      timestamp: new Date(file.timestamp).toLocaleString('zh-CN'),
      errorType: getErrorType(file.errorMessage).text
    }));

    // 字段映射
    const keyMap = {
      fileName: t('failedFiles.fileName'),
      errorMessage: t('failedFiles.errorMessage'),
      fileSize: t('common.fileSize'),
      fileType: t('common.type'),
      timestamp: t('failedFiles.time'),
      errorType: t('failedFiles.errorType')
    };

    const fileName = `上传失败文件列表_${new Date().toISOString().slice(0, 10)}`;

    try {
      if (type === 'csv') {
        downloadToCsv(exportData, keyMap, fileName);
        message.success(t('failedFiles.exportSuccessCsv'));
      } else {
        downloadToExcel(exportData, keyMap, fileName);
        message.success(t('failedFiles.exportSuccessExcel'));
      }
    } catch (error) {
      console.error('Export failed:', error);
      message.error(t('failedFiles.exportFailed'));
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN');
  };

  const getErrorType = (errorMessage: string) => {
    if (errorMessage.includes('文件太大了') || errorMessage.includes('too large')) {
      return {color: 'red', text: t('failedFiles.fileTooLarge')};
    } else if (errorMessage.includes('网络') || errorMessage.includes('network')) {
      return {color: 'orange', text: t('failedFiles.networkError')};
    } else if (errorMessage.includes('格式') || errorMessage.includes('format')) {
      return {color: 'blue', text: t('failedFiles.formatError')};
    } else {
      return {color: 'default', text: t('failedFiles.otherError')};
    }
  };

  if (failedFiles.length === 0) {
    return null;
  }

  return (
    <>
      <Tooltip title={t('failedFiles.tooltip', { count: failedFiles.length })}>
        <Button
          type="text"
          icon={<ExclamationCircleOutlined />}
          onClick={() => setIsModalVisible(true)}
          style={{ color: '#ff4d4f' }}
        >
          <Badge count={failedFiles.length} size="small">
            {t('failedFiles.failedFiles')}
          </Badge>
        </Button>
      </Tooltip>

      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            <span>{t('failedFiles.title')}</span>
            <Badge count={failedFiles.length} />
          </Space>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        footer={[
          <Dropdown
            key="export"
            menu={{
              items: [
                {
                  key: 'csv',
                  label: t('failedFiles.exportCsv'),
                  icon: <DownloadOutlined />,
                  onClick: () => handleExport('csv')
                },
                {
                  key: 'excel',
                  label: t('failedFiles.exportExcel'),
                  icon: <DownloadOutlined />,
                  onClick: () => handleExport('excel')
                }
              ]
            }}
            trigger={['click']}
          >
            <Button icon={<DownloadOutlined />}>
              {t('failedFiles.export')}
            </Button>
          </Dropdown>,
          <Button key="clear" danger onClick={handleClearAll}>
            {t('failedFiles.clearAll')}
          </Button>,
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            {t('failedFiles.close')}
          </Button>
        ]}
      >
        <List
          dataSource={failedFiles}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('failedFiles.totalFailed', { total }),
          }}
          renderItem={(item) => {
            const errorType = getErrorType(item.errorMessage);
            return (
              <List.Item
                actions={[
                  <Button
                    key="remove"
                    type="link"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveFile(item.fileName)}
                  >
                    {t('failedFiles.remove')}
                  </Button>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>{item.fileName}</Text>
                      <Tag color={errorType.color}>{errorType.text}</Tag>
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size="small">
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t('failedFiles.errorMessage')}: {item.errorMessage}
                      </Text>
                      <Space size="small">
                        {item.fileSize && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {t('failedFiles.size')}: {formatFileSize(item.fileSize)}
                          </Text>
                        )}
                        {item.fileType && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {t('failedFiles.type')}: {item.fileType}
                          </Text>
                        )}
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {t('failedFiles.time')}: {formatTimestamp(item.timestamp)}
                        </Text>
                      </Space>
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      </Modal>
    </>
  );
};

export default FailedFiles;
